import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTaskStatus } from '../utils/taskLabels';
import './TasksFiltersBar.css';

const EXPAND_STORAGE_KEY = 'tasks_filters_panel_expanded';
const STATUS_OPTIONS = ['pending', 'assigned', 'in_progress', 'completed'];
const MOBILE_MQ = '(max-width: 720px)';

function isMobileViewport() {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE_MQ).matches;
}

function expandStorageKey() {
  return `${EXPAND_STORAGE_KEY}:${isMobileViewport() ? 'mobile' : 'desktop'}`;
}

function getInitialExpanded() {
  if (typeof window === 'undefined') {
    return true;
  }
  try {
    const stored = window.localStorage.getItem(expandStorageKey());
    if (stored === 'true' || stored === 'false') {
      return stored === 'true';
    }
  } catch {
    // ignore storage errors
  }
  return !isMobileViewport();
}

const TasksFiltersBar = ({
  canUseWorkGroups,
  workGroups,
  workGroupFilterId,
  onWorkGroupFilterChange,
  filteredWorkGroup,
  onAssignAll,
  statusFilter,
  onStatusFilterChange,
  assignmentFilter,
  onAssignmentFilterChange,
  searchQuery,
  onSearchQueryChange,
  canToggleRemoved,
  includeRemoved,
  onToggleRemoved
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(getInitialExpanded);

  useEffect(() => {
    try {
      window.localStorage.setItem(expandStorageKey(), expanded ? 'true' : 'false');
    } catch {
      // ignore storage errors
    }
  }, [expanded]);

  const statusLabel = statusFilter
    ? formatTaskStatus(statusFilter, t)
    : t('tasks:filtersSummaryAllStatuses');

  const groupSummary = useMemo(() => {
    if (!canUseWorkGroups) {
      return null;
    }
    if (!workGroupFilterId || !filteredWorkGroup) {
      return t('tasks:filtersSummaryAllGroups');
    }
    return filteredWorkGroup.name;
  }, [canUseWorkGroups, workGroupFilterId, filteredWorkGroup, t]);

  const summaryText = useMemo(() => {
    const parts = [];
    if (groupSummary) {
      parts.push(groupSummary);
    }
    parts.push(statusLabel);
    if (assignmentFilter === 'unassigned') {
      parts.push(t('tasks:filtersUnassignedPill'));
    }
    if (String(searchQuery || '').trim()) {
      parts.push(t('tasks:filtersSummarySearchActive'));
    }
    if (includeRemoved) {
      parts.push(t('tasks:showRemovedToggle'));
    }
    return parts.join(' · ');
  }, [groupSummary, statusLabel, assignmentFilter, searchQuery, includeRemoved, t]);

  const activeChips = useMemo(() => {
    const chips = [];
    if (canUseWorkGroups && workGroupFilterId && filteredWorkGroup) {
      chips.push({ key: 'wg', label: filteredWorkGroup.name });
    }
    if (statusFilter) {
      chips.push({ key: 'status', label: statusLabel });
    }
    if (assignmentFilter === 'unassigned') {
      chips.push({ key: 'assign', label: t('tasks:filtersUnassignedPill') });
    }
    if (String(searchQuery || '').trim()) {
      chips.push({ key: 'search', label: t('tasks:filtersSummarySearchActive'), neutral: true });
    }
    if (includeRemoved) {
      chips.push({ key: 'removed', label: t('tasks:showRemovedToggle'), neutral: true });
    }
    return chips;
  }, [
    canUseWorkGroups,
    workGroupFilterId,
    filteredWorkGroup,
    statusFilter,
    statusLabel,
    assignmentFilter,
    searchQuery,
    includeRemoved,
    t
  ]);

  return (
    <section
      className="tasks-filters-panel"
      data-open={expanded ? 'true' : 'false'}
      aria-labelledby="tasks-filters-heading"
    >
      <button
        type="button"
        className="tasks-filters-toggle"
        aria-expanded={expanded}
        aria-controls="tasks-filters-body"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="tasks-filters-toggle-icon" aria-hidden="true">
          <span className="material-symbols-outlined">filter_list</span>
        </span>
        <span className="tasks-filters-toggle-text">
          <span id="tasks-filters-heading" className="tasks-filters-toggle-title">
            {t('tasks:filtersLabel')}
          </span>
          <span className="tasks-filters-toggle-sub">{summaryText}</span>
        </span>
        {activeChips.length > 0 && (
          <span className="tasks-filters-active-chips" aria-hidden="true">
            {activeChips.map((chip) => (
              <span
                key={chip.key}
                className={`tasks-filters-active-chip${chip.neutral ? ' tasks-filters-active-chip--neutral' : ''}`}
              >
                {chip.label}
              </span>
            ))}
          </span>
        )}
        <span className="material-symbols-outlined tasks-filters-chev" aria-hidden="true">
          expand_more
        </span>
      </button>

      <div
        id="tasks-filters-body"
        className="tasks-filters-body"
        hidden={!expanded}
      >
        {canUseWorkGroups && (
          <div className="tasks-filter-group">
            <div className="tasks-filter-group-header">
              <span className="tasks-filter-group-label">{t('tasks:filtersGroupWorkGroups')}</span>
              <span className="tasks-filter-group-hint">{t('tasks:filtersGroupHintSingle')}</span>
            </div>
            <div className="tasks-filter-group-controls">
              <div
                className="tasks-filter-group-row tasks-filter-group-row--scroll"
                role="group"
                aria-label={t('tasks:filtersGroupWorkGroups')}
              >
                <button
                  type="button"
                  className={`btn tasks-filters-pill ${!workGroupFilterId ? 'btn-primary' : 'btn-secondary'}`}
                  aria-pressed={!workGroupFilterId}
                  onClick={() => onWorkGroupFilterChange('')}
                >
                  {t('tasks:filtersAllPill')}
                </button>
                {workGroups.map((group) => {
                  const active = String(workGroupFilterId) === String(group.id);
                  return (
                    <button
                      key={group.id}
                      type="button"
                      className={`btn tasks-filters-pill ${active ? 'btn-primary' : 'btn-secondary'}`}
                      aria-pressed={active}
                      onClick={() => onWorkGroupFilterChange(String(group.id))}
                    >
                      {group.name}
                    </button>
                  );
                })}
              </div>
              {filteredWorkGroup && (
                <button
                  type="button"
                  className="btn btn-primary tasks-filters-assign-all"
                  onClick={onAssignAll}
                >
                  {t('tasks:workGroupAssignAll')}
                </button>
              )}
            </div>
          </div>
        )}

        <div className="tasks-filter-group">
          <div className="tasks-filter-group-header">
            <span className="tasks-filter-group-label">{t('tasks:filtersGroupStatus')}</span>
            <span className="tasks-filter-group-hint">{t('tasks:filtersGroupHintSingle')}</span>
          </div>
          <div
            className="tasks-filter-group-row tasks-filter-group-row--scroll"
            role="group"
            aria-label={t('tasks:filtersGroupStatus')}
          >
            <button
              type="button"
              className={`btn tasks-filters-pill ${!statusFilter ? 'btn-primary' : 'btn-secondary'}`}
              aria-pressed={!statusFilter}
              onClick={() => onStatusFilterChange('')}
            >
              {t('tasks:filtersAllPill')}
            </button>
            {STATUS_OPTIONS.map((status) => {
              const active = statusFilter === status;
              return (
                <button
                  key={status}
                  type="button"
                  className={`btn tasks-filters-pill ${active ? 'btn-primary' : 'btn-secondary'}`}
                  aria-pressed={active}
                  onClick={() => onStatusFilterChange(status)}
                >
                  {formatTaskStatus(status, t)}
                </button>
              );
            })}
          </div>
        </div>

        <div className="tasks-filter-group">
          <div className="tasks-filter-group-header">
            <span className="tasks-filter-group-label">{t('tasks:filtersGroupAssignment')}</span>
            <span className="tasks-filter-group-hint">{t('tasks:filtersGroupHintQuick')}</span>
          </div>
          <div
            className="tasks-filter-group-row"
            role="group"
            aria-label={t('tasks:filtersGroupAssignment')}
          >
            <button
              type="button"
              className={`btn tasks-filters-pill ${!assignmentFilter ? 'btn-primary' : 'btn-secondary'}`}
              aria-pressed={!assignmentFilter}
              onClick={() => onAssignmentFilterChange('')}
            >
              {t('tasks:filtersAllPill')}
            </button>
            <button
              type="button"
              className={`btn tasks-filters-pill tasks-filters-pill--ghost ${
                assignmentFilter === 'unassigned' ? 'btn-primary is-active' : 'btn-secondary'
              }`}
              aria-pressed={assignmentFilter === 'unassigned'}
              onClick={() => onAssignmentFilterChange('unassigned')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">person_off</span>
              <span>{t('tasks:filtersUnassignedPill')}</span>
            </button>
          </div>
        </div>

        <div className="tasks-filter-group">
          <div className="tasks-filter-group-header">
            <span className="tasks-filter-group-label">{t('tasks:filtersGroupSearchMore')}</span>
          </div>
          <div className="tasks-filters-footer">
            <div className="tasks-filters-search">
              <span className="material-symbols-outlined tasks-filters-search-icon" aria-hidden="true">
                search
              </span>
              <input
                id="tasks-filters-search"
                type="search"
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                placeholder={t('tasks:filtersSearchPlaceholder')}
                aria-label={t('tasks:filtersSearchAria')}
              />
            </div>

            {canToggleRemoved && (
              <button
                type="button"
                className={`btn tasks-filters-removed ${includeRemoved ? 'btn-primary' : 'btn-secondary'}`}
                onClick={onToggleRemoved}
                aria-pressed={includeRemoved}
                title={includeRemoved ? t('tasks:hideRemovedToggle') : t('tasks:showRemovedToggle')}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  {includeRemoved ? 'visibility_off' : 'visibility'}
                </span>
                <span>
                  {includeRemoved ? t('tasks:hideRemovedToggle') : t('tasks:showRemovedToggle')}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TasksFiltersBar;
